import React, { useState } from "react";
import * as XLSX from "xlsx";
import { Upload, CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";
import { Modal } from "./ui/Modal";
import { importForUser, requestImportCode, verifyImportCode } from "../business/AdminBusiness";
import toast from "react-hot-toast";

// Importa na conta da usuária a planilha gerada pela extensão agendix-migrador (Minha Agenda).
// Lê o .xlsx no navegador, mostra quanto tem em cada aba e manda em lotes pro backend
// (POST /admin/users/:id/import/:kind), na ordem serviços → estoque → clientes → agendamentos:
// os clientes devolvem o mapa id-no-Minha-Agenda → _id, usado pra ligar cada agendamento.
// O backend só adiciona: o que já existe na conta é pulado, nada é alterado ou apagado.
// Antes, a dona da conta autoriza: o painel manda um código pro WhatsApp ou e-mail dela, o admin
// digita o que ela passar e recebe um token de 3 h, enviado em cada lote (sem ele o backend recusa).

const BATCH = 100;

const SHEETS = [
  { kind: "services", sheet: "Serviços", label: "Serviços" },
  { kind: "stocks", sheet: "Estoque", label: "Estoque" },
  { kind: "clients", sheet: "Clientes", label: "Clientes" },
  { kind: "appointments", sheet: "Agendamentos", label: "Agendamentos" },
];

const maskPhone = (phone) => { const d = String(phone || "").replace(/\D/g, ""); return d.length >= 8 ? `••••${d.slice(-4)}` : ""; };
const maskEmail = (email) => { const [name, domain] = String(email || "").split("@"); return domain ? `${name.slice(0, 2)}***@${domain}` : ""; };

const str = (v) => (v === null || v === undefined ? "" : String(v).trim());
// "45", "45,5" ou "R$ 45,50" -> 45.5
const toNumber = (v) => { const n = parseFloat(str(v).replace(/[^\d,.-]/g, "").replace(",", ".")); return Number.isFinite(n) ? n : 0; };

// Linha da planilha (colunas da extensão) → formato que o backend espera
const TO_ROW = {
  services: (r) => ({ name: r["Serviço"], value: r["Valor"], durationMinutes: r["Duração (min)"], description: r["Descrição"] }),
  stocks: (r) => ({ name: r["Produto"], current: r["Estoque atual"], minimum: r["Estoque mínimo"], unitValue: r["Valor unitário (custo)"] }),
  clients: (r) => ({
    externalId: str(r["ID Minha Agenda"]),
    name: r["Nome"],
    phone: r["Telefone"],
    email: r["E-mail"],
    birthDate: r["Data de nascimento"],
    observations: r["Observações"],
  }),
  appointments: (r, clientMap) => {
    // Valor, pagamento e profissional não têm campo no agendamento do Agendix: vão pra descrição
    const details = [
      str(r["Valor"]) && `Valor: R$ ${str(r["Valor"])}`,
      str(r["Profissional"]) && `Profissional: ${str(r["Profissional"])}`,
      str(r["Observações"]),
    ].filter(Boolean);
    return {
      date: str(r["Data"]),
      hours: str(r["Hora"]),
      clientId: clientMap[str(r["ID cliente Minha Agenda"])] || null,
      clientName: str(r["Cliente"]),
      services: str(r["Serviços"]).split(",").map((s) => s.trim()).filter(Boolean),
      status: str(r["Status"]),
      // Vira entrada no financeiro. Planilha antiga (sem "Valor pago") cai no valor cheio se estava paga
      paidValue: r["Valor pago"] !== undefined ? toNumber(r["Valor pago"]) : str(r["Pago"]) === "sim" ? toNumber(r["Valor"]) : 0,
      paymentMethod: str(r["Forma de pagamento"]),
      description: details.join(" · "),
    };
  },
};

export const ImportMinhaAgendaModal = ({ user, onClose, onDone }) => {
  const [fileName, setFileName] = useState("");
  const [data, setData] = useState(null); // { services: [...linhas da planilha], ... }
  const [selected, setSelected] = useState({ services: true, stocks: true, clients: true, appointments: true });
  const phoneLabel = maskPhone(user?.phone);
  const emailLabel = maskEmail(user?.email);
  const [channel, setChannel] = useState(phoneLabel ? "whatsapp" : "email");
  const [codeSentTo, setCodeSentTo] = useState("");
  const [code, setCode] = useState("");
  const [importToken, setImportToken] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null); // { label, done, total }
  const [results, setResults] = useState(null);

  const readFile = async (file) => {
    if (!file) return;
    try {
      const book = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const parsed = {};
      SHEETS.forEach(({ kind, sheet }) => {
        const ws = book.Sheets[sheet];
        parsed[kind] = ws ? XLSX.utils.sheet_to_json(ws, { raw: false, defval: "" }).filter((r) => Object.values(r).some((v) => str(v))) : [];
      });
      if (!SHEETS.some(({ kind }) => parsed[kind].length)) {
        toast.error("Essa planilha não tem as abas da exportação do Minha Agenda.");
        return;
      }
      setFileName(file.name);
      setData(parsed);
      setResults(null);
    } catch (err) {
      console.error("Erro ao ler planilha:", err);
      toast.error("Não foi possível ler essa planilha.");
    }
  };

  const run = async () => {
    setRunning(true);
    const summary = {};
    let clientMap = {};
    try {
      for (const { kind, label } of SHEETS) {
        if (!selected[kind] || !data[kind].length) continue;
        const rows = data[kind];
        const total = { created: 0, skipped: 0, createdServices: 0, createdClients: 0, transactions: 0 };
        for (let i = 0; i < rows.length; i += BATCH) {
          setProgress({ label, done: i, total: rows.length });
          const mapNow = clientMap;
          const batch = rows.slice(i, i + BATCH).map((r) => TO_ROW[kind](r, mapNow));
          const { data: res } = await importForUser(user._id, kind, batch, importToken);
          total.created += res.created || 0;
          total.skipped += res.skipped || 0;
          total.createdServices += res.createdServices || 0;
          total.createdClients += res.createdClients || 0;
          total.transactions += res.transactions || 0;
          if (res.map) clientMap = { ...clientMap, ...res.map };
        }
        summary[kind] = total;
        setResults({ ...summary });
      }
      setProgress(null);
      toast.success("Importação concluída.");
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "A importação parou no meio. O que já entrou fica na conta; rode de novo que o resto é completado sem duplicar.");
    } finally {
      setRunning(false);
    }
  };

  const nothingSelected = !data || !SHEETS.some(({ kind }) => selected[kind] && data[kind].length);

  const sendCode = async () => {
    setAuthBusy(true);
    try {
      const { data: res } = await requestImportCode(user._id, channel);
      setCodeSentTo(res.sentTo);
      setCode("");
      toast.success(`Código enviado por ${res.sentTo}.`);
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Não foi possível enviar o código.");
    } finally {
      setAuthBusy(false);
    }
  };

  const confirmCode = async () => {
    setAuthBusy(true);
    try {
      const { data: res } = await verifyImportCode(user._id, code);
      setImportToken(res.token);
      toast.success("Importação autorizada pela usuária.");
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Código inválido.");
    } finally {
      setAuthBusy(false);
    }
  };

  return (
    <Modal title={`Importar do Minha Agenda · ${user?.username || ""}`} isOpen onClose={running ? () => {} : onClose} maxWidth="max-w-lg">
      <p className="mb-4 text-sm text-gray-500">
        Envie a planilha gerada pela extensão <b>Agendix · Migrar do Minha Agenda</b>. A importação só <b>adiciona</b>:
        clientes (mesmo telefone ou nome), serviços e produtos (mesmo nome) e agendamentos (mesma cliente, dia e hora) que já
        existem na conta são pulados. Nada é alterado ou apagado.
      </p>

      <label className="mb-4 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 px-4 py-6 text-center hover:border-accent hover:bg-gray-50">
        <Upload className="mb-2 h-6 w-6 text-gray-400" />
        <span className="text-sm font-medium text-gray-700">{fileName || "Escolher planilha .xlsx"}</span>
        <input type="file" accept=".xlsx" className="hidden" disabled={running} onChange={(e) => readFile(e.target.files?.[0])} />
      </label>

      {data && (
        <div className="mb-4 space-y-2">
          {SHEETS.map(({ kind, label }) => (
            <label key={kind} className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm">
              <span className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selected[kind] && data[kind].length > 0}
                  disabled={running || !data[kind].length}
                  onChange={(e) => setSelected((prev) => ({ ...prev, [kind]: e.target.checked }))}
                />
                {label}
              </span>
              <span className="text-gray-500">
                {results?.[kind]
                  ? `${results[kind].created} novos · ${results[kind].skipped} já existiam`
                  : `${data[kind].length} na planilha`}
              </span>
            </label>
          ))}
          {selected.appointments && !selected.clients && data.appointments.length > 0 && (
            <p className="flex items-start gap-1.5 text-xs text-amber-600">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Sem importar clientes, os agendamentos são ligados por nome e quem não existir é criada só com o nome.
            </p>
          )}
        </div>
      )}

      {progress && (
        <div className="mb-4">
          <p className="mb-1 text-xs text-gray-500">Importando {progress.label.toLowerCase()}… {progress.done} de {progress.total}</p>
          <div className="h-2 overflow-hidden rounded-full bg-gray-100">
            <div className="h-full bg-accent transition-all" style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} />
          </div>
        </div>
      )}

      {results && !running && (
        <div className="mb-4 flex items-start gap-2 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Pronto.
            {results.appointments?.transactions > 0 && ` ${results.appointments.transactions} agendamento(s) pago(s) viraram entrada no financeiro.`}
            {results.appointments?.createdServices > 0 && ` ${results.appointments.createdServices} serviço(s) que só apareciam em agendamentos foram criados.`}
            {results.appointments?.createdClients > 0 && ` ${results.appointments.createdClients} cliente(s) que só apareciam em agendamentos foram criadas.`}
          </span>
        </div>
      )}

      {data && !results && (
        <div className="mb-4 rounded-lg border border-gray-200 p-3">
          <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-800">
            <ShieldCheck className="h-4 w-4 text-accent" /> Autorização da usuária
          </p>
          {importToken ? (
            <p className="flex items-center gap-1.5 text-sm text-green-700">
              <CheckCircle2 className="h-4 w-4" /> Autorizado pela usuária. Vale por 3 horas.
            </p>
          ) : (
            <>
              <p className="mb-2 text-xs text-gray-500">Mande um código pra dona da conta e digite o que ela te passar.</p>
              <div className="mb-2 flex flex-wrap gap-2">
                {[
                  { value: "whatsapp", label: phoneLabel ? `WhatsApp ${phoneLabel}` : "WhatsApp (sem número)", enabled: !!phoneLabel },
                  { value: "email", label: emailLabel ? `E-mail ${emailLabel}` : "E-mail (sem e-mail)", enabled: !!emailLabel },
                ].map((opt) => (
                  <label key={opt.value} className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs ${channel === opt.value ? "border-accent text-gray-900" : "border-gray-200 text-gray-600"} ${opt.enabled ? "cursor-pointer" : "opacity-50"}`}>
                    <input type="radio" name="import-channel" checked={channel === opt.value} disabled={!opt.enabled || authBusy} onChange={() => setChannel(opt.value)} />
                    {opt.label}
                  </label>
                ))}
              </div>
              <button
                type="button"
                className="mb-2 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                onClick={sendCode}
                disabled={authBusy || running || (channel === "whatsapp" ? !phoneLabel : !emailLabel)}
              >
                {codeSentTo ? "Mandar outro código" : "Enviar código"}
              </button>
              {codeSentTo && (
                <div className="flex items-center gap-2">
                  <input
                    className="w-32 rounded-md border border-gray-300 px-3 py-1.5 text-center text-sm tracking-[0.3em] focus:outline-none focus:ring-2 focus:ring-accent"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  />
                  <button
                    type="button"
                    className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-60"
                    onClick={confirmCode}
                    disabled={authBusy || code.length !== 6}
                  >
                    Confirmar
                  </button>
                  <span className="text-xs text-gray-400">enviado por {codeSentTo}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <button className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60" onClick={onClose} disabled={running}>
          {results ? "Fechar" : "Cancelar"}
        </button>
        {!results && (
          <button
            className="flex-1 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-60"
            onClick={run}
            disabled={running || nothingSelected || !importToken}
          >
            {running ? "Importando…" : "Importar"}
          </button>
        )}
      </div>
    </Modal>
  );
};
